import React, { Component } from 'react'
import Layout from './Layout';
import { Button, Breadcrumb, Switch, Tag, Spin, Select, Popover, InputNumber, Dropdown, Menu, Icon, Modal, message } from 'antd';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { adminRoutes } from '../helpers/adminRoutes';
import { getSupplies } from '../redux/actions/supply';
import { addSupply } from '../redux/actions/addSupply';
import { deleteSupply } from '../redux/actions/deleteSupply';
import { cloneSupply } from '../redux/actions/cloneSupply';
import { SERVER_URL } from '../api';
import InlineEdit from './InlineEdit';
import SupplyImagesModal from './SupplyImagesModal';

const { Option } = Select;

class SupplyList extends Component {

    constructor(props) {
        super(props)
        this.state = {
            loadingApi: true,
            savingKey: null,
            managingImagesFor: null,
            lowStockOnly: false,
            lowStockCutoff: '', // blank = each variant's own (or the default) threshold; a number overrides all of them for this filter
        }
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        let { dispatch } = this.props;
        this.setState({ loadingApi: true });
        dispatch(getSupplies({ start: 0, length: 0 })).then(() => {
            this.setState({ loadingApi: false });
        }).catch(() => {
            this.setState({ loadingApi: false });
        })
    }

    handleDelete = (id) => {
        let { dispatch } = this.props;
        dispatch(deleteSupply(id)).then(() => {
            this.load();
        })
    }

    // Modal.confirm rather than Popconfirm — Popconfirm doesn't play well
    // nested inside a Dropdown's Menu (the menu closes before it can show).
    confirmDelete = (id) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this supply?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => this.handleDelete(id),
        });
    }

    // Clones straight into the edit screen so you can immediately adjust the
    // copy (e.g. relabel needle sizes) instead of retyping everything.
    handleClone = (e, id) => {
        e.stopPropagation();
        let { dispatch, history } = this.props;
        dispatch(cloneSupply(id)).then(res => {
            let cloned = res && res.data && res.data.data;
            if (cloned && cloned._id) {
                history.push(adminRoutes.EDITSUPPLY + "/" + cloned._id);
            } else {
                this.load();
            }
        }).catch(() => {
            message.error('Could not clone this supply.');
        })
    }

    handleAdd = () => {
        this.props.history.push(adminRoutes.ADDSUPPLY);
    }

    handleEdit = (id) => {
        this.props.history.push(adminRoutes.EDITSUPPLY + "/" + id);
    }

    // Product-level partial update — name, shopVisible, status.
    updateSupplyField = (supply, field, value) => {
        let { dispatch } = this.props;
        let key = supply._id + '_' + field;
        this.setState({ savingKey: key });
        dispatch(addSupply({ id: supply._id, [field]: value }, true)).then(() => {
            this.setState({ savingKey: null });
            this.load();
        }).catch(() => {
            this.setState({ savingKey: null });
            message.error('Could not save.');
        })
    }

    // Product position — the backend already shifts every other Supply's
    // position server-side when given a new value, so just send it.
    reorderSupplyPosition = (supply, newPosition) => {
        this.updateSupplyField(supply, 'position', newPosition);
    }

    // Variant-level field update — sends the full variants array back (the
    // backend always replaces it wholesale), with just this one field on
    // this one variant changed.
    updateVariantField = (supply, variantId, field, value) => {
        let { dispatch } = this.props;
        let key = supply._id + '_' + variantId + '_' + field;

        let variants = supply.variants.map(v => v._id === variantId ? { ...v, [field]: value } : v);

        this.setState({ savingKey: key });
        dispatch(addSupply({ id: supply._id, variants }, true)).then(() => {
            this.setState({ savingKey: null });
            this.load();
        }).catch(() => {
            this.setState({ savingKey: null });
            message.error('Could not save.');
        })
    }

    // Variant position — unlike Supply.position, the backend does a straight
    // whole-array replace with no shift logic (variants live inside one
    // document, edited together), so the shift has to be computed here:
    // moving to an earlier slot pushes everything in between back by one,
    // moving later pulls everything in between forward by one. Without this,
    // setting one variant's position just collides with whichever sibling
    // already held that value.
    reorderVariantPosition = (supply, variantId, newPosition) => {
        let { dispatch } = this.props;
        let moving = supply.variants.find(v => v._id === variantId);
        if (!moving || moving.position === newPosition) return;
        let oldPosition = moving.position;

        let variants = supply.variants.map(v => {
            if (v._id === variantId) return { ...v, position: newPosition };
            if (oldPosition < newPosition && v.position > oldPosition && v.position <= newPosition) {
                return { ...v, position: v.position - 1 };
            }
            if (oldPosition > newPosition && v.position >= newPosition && v.position < oldPosition) {
                return { ...v, position: v.position + 1 };
            }
            return v;
        });

        let key = supply._id + '_' + variantId + '_position';
        this.setState({ savingKey: key });
        dispatch(addSupply({ id: supply._id, variants }, true)).then(() => {
            this.setState({ savingKey: null });
            this.load();
        }).catch(() => {
            this.setState({ savingKey: null });
            message.error('Could not save.');
        })
    }

    // A variant counts as low stock either by its own (or the global default)
    // threshold — already computed server-side as isLowStock — or, when the
    // cutoff field is filled in, by that single number overriding every
    // variant's own threshold for this one-off look.
    isLowStockMatch = (variant) => {
        let { lowStockCutoff } = this.state;
        if (variant.quantity === null || variant.quantity === undefined) return false;
        if (lowStockCutoff !== '' && lowStockCutoff !== null && lowStockCutoff !== undefined) {
            return variant.quantity <= lowStockCutoff;
        }
        return !!variant.isLowStock;
    }

    render() {
        let { data } = this.props;
        let supplies = (data && data.data && data.data.data) || [];
        let { loadingApi, managingImagesFor, lowStockOnly, lowStockCutoff } = this.state;

        return (
            <Layout>
                <div className="content-box supplies-page">
                    <div className="bredcrumb-div">
                        <Breadcrumb separator="-">
                            <Breadcrumb.Item><Link to={adminRoutes.SUPPLYLIST}>Supplies</Link></Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                    <div className="page-title d-flex">
                        <h2>Supplies</h2>
                        <div>
                            <Link to={adminRoutes.CATEGORYLIST}><Button style={{ marginRight: 8 }}>Categories</Button></Link>
                            <Button onClick={this.handleAdd} type="primary" className="primary-btn"> ADD SUPPLY </Button>
                        </div>
                    </div>
                    <div className="supply-filter-bar">
                        <label style={{ marginRight: 8 }}>Low stock only</label>
                        <Switch checked={lowStockOnly} onChange={(val) => this.setState({ lowStockOnly: val })} />
                        {lowStockOnly && (
                            <span style={{ marginLeft: 16 }}>
                                <label style={{ marginRight: 8 }}>Qty at or below <em>(blank = each option's own threshold)</em></label>
                                <InputNumber min={0} placeholder="default" value={lowStockCutoff} onChange={(val) => this.setState({ lowStockCutoff: val === undefined ? '' : val })} />
                            </span>
                        )}
                    </div>
                    <div className="page-content">
                        {loadingApi ? <Spin /> : supplies.map(supply => {
                            let variants = supply.variants || [];
                            let visibleVariants = lowStockOnly ? variants.filter(this.isLowStockMatch) : variants;
                            if (lowStockOnly && visibleVariants.length === 0) return null;
                            let images = supply.images || [];

                            return (
                                <div className="supply-group" key={supply._id}>
                                    <div
                                        className="supply-header-row"
                                        onClick={() => this.handleEdit(supply._id)}
                                        title="Click to edit this supply"
                                    >
                                        <div
                                            className={`supply-thumb-stack ${images.length > 1 ? 'stacked' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); this.setState({ managingImagesFor: supply }); }}
                                            title="Manage photos"
                                        >
                                            {images.length > 0 ? images.slice(0, 3).map((img, i) => (
                                                <img key={i} className="supply-thumb" src={`${SERVER_URL}uploads/${img}`} alt="" />
                                            )) : <div className="supply-thumb supply-thumb-empty"><span>+</span></div>}
                                            {images.length > 3 && <span className="supply-thumb-more">+{images.length - 2}</span>}
                                        </div>
                                        <span className="supply-name" onClick={(e) => e.stopPropagation()}>
                                            <InlineEdit
                                                value={supply.name}
                                                onSave={(val) => this.updateSupplyField(supply, 'name', val)}
                                            />
                                        </span>
                                        <div className="supply-meta" onClick={(e) => e.stopPropagation()}>
                                            <div className="supply-meta-field">
                                                <label>Show in shop</label>
                                                <Switch
                                                    size="small"
                                                    checked={!!supply.shopVisible}
                                                    onChange={(val) => this.updateSupplyField(supply, 'shopVisible', val)}
                                                />
                                            </div>
                                            <div className="supply-meta-field">
                                                <label>Published</label>
                                                <Switch
                                                    size="small"
                                                    checked={!!supply.status}
                                                    onChange={(val) => this.updateSupplyField(supply, 'status', val)}
                                                />
                                            </div>
                                            <div className="supply-meta-field">
                                                <label>Position</label>
                                                <Select
                                                    size="small"
                                                    style={{ width: 60 }}
                                                    value={supply.position}
                                                    onChange={(val) => this.reorderSupplyPosition(supply, val)}
                                                >
                                                    {supplies.map((_, i) => <Option key={i} value={i}>{i + 1}</Option>)}
                                                </Select>
                                            </div>
                                            <Dropdown
                                                trigger={['click']}
                                                overlay={(
                                                    <Menu>
                                                        <Menu.Item onClick={() => this.handleEdit(supply._id)}>Edit</Menu.Item>
                                                        <Menu.Item onClick={(e) => this.handleClone(e.domEvent, supply._id)}>Clone</Menu.Item>
                                                        <Menu.Item onClick={() => this.confirmDelete(supply._id)}>Delete</Menu.Item>
                                                    </Menu>
                                                )}
                                            >
                                                <Button size="small" icon="ellipsis" onClick={(e) => e.stopPropagation()} />
                                            </Dropdown>
                                        </div>
                                    </div>

                                    {visibleVariants.map(variant => {
                                        // Defensive: a stray unresolved "__pending:N" placeholder
                                        // (saved by an older backend build that didn't know the
                                        // convention yet) should fall back to the gallery default
                                        // rather than render as a broken image.
                                        let rawImage = (variant.image && String(variant.image).startsWith('__pending:')) ? null : variant.image;
                                        let variantThumb = rawImage || images[0];
                                        return (
                                            <div className="supply-option-row" key={variant._id}>
                                                <div className="supply-option-col supply-option-col-photo">
                                                    {variantThumb && (
                                                        <Popover
                                                            trigger="click"
                                                            title="Assign a photo"
                                                            content={(
                                                                <div className="option-image-picker">
                                                                    <div
                                                                        className={`option-image-picker-choice ${!rawImage ? 'selected' : ''}`}
                                                                        onClick={() => this.updateVariantField(supply, variant._id, 'image', null)}
                                                                    >
                                                                        Use default ({images[0] ? 'first product photo' : 'none'})
                                                                    </div>
                                                                    <div className="option-image-picker-grid">
                                                                        {images.map((img) => (
                                                                            <img
                                                                                key={img}
                                                                                src={`${SERVER_URL}uploads/${img}`}
                                                                                alt=""
                                                                                className={`option-image-picker-thumb ${rawImage === img ? 'selected' : ''}`}
                                                                                onClick={() => this.updateVariantField(supply, variant._id, 'image', img)}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                    <Button size="small" style={{ marginTop: 8 }} onClick={() => this.setState({ managingImagesFor: supply })}>
                                                                        Add/reorder photos…
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        >
                                                            <img
                                                                className="supply-option-thumb"
                                                                src={`${SERVER_URL}uploads/${variantThumb}`}
                                                                alt=""
                                                                title="Assign a photo"
                                                            />
                                                        </Popover>
                                                    )}
                                                </div>
                                                <div className="supply-option-col supply-option-col-label">
                                                    <label>Label</label>
                                                    <span title={variant.optionLabel}>
                                                        <InlineEdit
                                                            value={variant.optionLabel}
                                                            placeholder="Untitled"
                                                            onSave={(val) => this.updateVariantField(supply, variant._id, 'optionLabel', val)}
                                                        />
                                                    </span>
                                                </div>
                                                <div className="supply-option-col supply-option-col-price">
                                                    <label>Price</label>
                                                    <InlineEdit
                                                        type="number"
                                                        min={0}
                                                        prefix="$"
                                                        value={variant.price}
                                                        onSave={(val) => this.updateVariantField(supply, variant._id, 'price', val)}
                                                    />
                                                </div>
                                                <div className="supply-option-col supply-option-col-quantity">
                                                    <label>Quantity</label>
                                                    <InlineEdit
                                                        type="number"
                                                        min={0}
                                                        placeholder="Unlimited"
                                                        value={variant.quantity}
                                                        onSave={(val) => this.updateVariantField(supply, variant._id, 'quantity', val)}
                                                    />
                                                </div>
                                                <div className="supply-option-col supply-option-col-position">
                                                    <label>Position</label>
                                                    <Select
                                                        size="small"
                                                        style={{ width: 60 }}
                                                        value={variant.position}
                                                        onChange={(val) => this.reorderVariantPosition(supply, variant._id, val)}
                                                    >
                                                        {variants.map((_, i) => <Option key={i} value={i}>{i + 1}</Option>)}
                                                    </Select>
                                                </div>
                                                <div className="supply-option-col supply-option-col-lowstock">
                                                    {variant.isLowStock && <Tag color="red" className="low-stock-tag">Low stock</Tag>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {managingImagesFor && (
                    <SupplyImagesModal
                        supply={managingImagesFor}
                        onClose={() => this.setState({ managingImagesFor: null })}
                        onSaved={this.load}
                    />
                )}
            </Layout>
        )
    }
}

const mapStateToProps = ((state) => {
    let { loading, data, error } = state.getSupplies
    return {
        loading,
        data,
        error,
    }
})

export default connect(mapStateToProps)(SupplyList);
